import {gql} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {Box, Flex, HoverCard, Stack, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import AntibodyFormatCreationPane from 'antibodies/format/AntibodyFormatCreationPane';
import {
  AntibodyFormatCreationPermissionQuery,
  AntibodyFormatCreationPermissionQueryVariables,
} from 'antibodies/schema-configuration/__generated/AntibodyFormatCreationPermissionQuery';
import {canUseBiologicsFeature, isBiologicsEnabled} from 'antibodies/util/biologicsCapability';
import Antibody, {AntibodyProps} from 'antibodies/visualization/Antibody';
import {CoreObjectId} from 'app-core/framework/CoreObjectConstants';
import * as ArchiveDAO from 'archive/ArchiveDAO';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import {assert} from 'Asserts';
import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import {BiologicsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {ANTIBODIES_ANALYTICS_ACTIONS} from 'constants/analytics-products/AntibodiesAnalyticsConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import OverflowMenu, {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import Section from 'ui-library/menus/Section';
import PlainSearchInput, {ClearType, BorderRadius} from 'ui-library/PlainSearchInput';
import {InputChangeEvent} from 'util/Types';

import {AntibodyFormatGraphQL} from '../../../graphql-fragments/antibodies/__generated/AntibodyFormatGraphQL';
import {FormatSchemaUsage, FormatSchemaUsageMap} from './buildFormatSchemaUsageMap';
import FormatSchemaCountBadge from './FormatSchemaCountBadge';

export const ANTIBODY_FORMAT_CREATION_PERMISSION_QUERY = gql`
  query AntibodyFormatCreationPermissionQuery @skipAudit @engComponent(name: MOLBIO) {
    currentTenant {
      id
      permissions {
        canCreateAntibodyFormats
      }
    }
  }
`;

type ArchiveFilter = 'all' | 'archived';

const ARCHIVE_FILTER_OPTIONS: Array<OptionData> = [
  {value: 'all', name: 'All formats'},
  {value: 'archived', name: 'Archived formats'},
];

export type AntibodyTileProps = AntibodyProps & {disabled: boolean};

const PROTEIN_FORMAT_AUDIT_LOG_MODAL_TITLE = 'Protein format audit log';

/** Built-in format registry IDs use this prefix; they are not DB-backed and have no audit rows. */
const BUILTIN_ANTIBODY_FORMAT_ID_PREFIX = 'builtinabfmt_';

export interface AntibodyFormatPickerProps {
  antibodyTileProps: Array<AntibodyTileProps>;
  disabled: boolean;
  selectedFormat: string | null;
  setSelectedFormat: (formatId: string) => void;
  /** "Create new" button will only be shown to users with permission to create new formats. */
  enableCreateNew?: boolean;
  onFormatCreated?: (formatId: string) => Promise<void>;
  /** Called when the format creation pane opens or closes. Use to hide format fields while creating. */
  onCreationPaneOpenChange: (isOpen: boolean) => void;
  /** Analytics are tracked when metadata is provided, and not tracked when metadata is not provided. */
  analyticsMetadata?: AnalyticsMetadataForContext;
  /** Shows the audit log button next to search. Intended for schema settings surface only. */
  showAuditLogButton?: boolean;
  /** Called after a format is archived or unarchived so the parent can refetch. */
  onArchiveChange?: () => Promise<void>;
  /** Enables the overflow context menu (archive, edit) on format tiles. Intended for schema settings surface only. */
  enableFormatManagement?: boolean;
  /** When provided, shows schema usage count badges and tooltips on format tiles. */
  formatSchemaUsageMap?: FormatSchemaUsageMap;
}

interface FormatTileProps {
  formatId: string;
  label: string;
  onClick: () => void;
  isSelected: boolean;
  disabled?: boolean;
  isHovered: boolean;
  setHoveredFormatId: (formatId: string | null) => void;
  children: React.ReactNode;
  wrapperClassName?: string;
  labelClassName?: string;
  isArchived?: boolean;
  overflowMenuOptions?: Array<OverflowMenuOption>;
  schemaUsage?: FormatSchemaUsage;
}

function getGlyphWrapperClassName(isSelected: boolean, disabled: boolean, isHovered: boolean): string {
  let stylesToUse = null;
  if (isSelected) {
    stylesToUse = styles.selectedTileGlyphWrapper;
  } else if (disabled && isHovered) {
    stylesToUse = styles.disabledHoveredTileGlyphWrapper;
  } else if (disabled) {
    stylesToUse = styles.disabledTileGlyphWrapper;
  } else if (isHovered) {
    stylesToUse = styles.hoveredTileGlyphWrapper;
  }
  return css(stylesToUse);
}

function FormatTile({
  formatId,
  label,
  onClick,
  isSelected,
  disabled = false,
  isHovered,
  setHoveredFormatId,
  children,
  wrapperClassName,
  labelClassName,
  isArchived: isArchivedTile = false,
  overflowMenuOptions,
  schemaUsage,
}: FormatTileProps): JSX.Element {
  let schemaUsageBadge: React.ReactNode = null;
  if (schemaUsage != null) {
    const tooltipHeader =
      schemaUsage.count === 0
        ? 'No schemas'
        : schemaUsage.count === 1
          ? '1 schema'
          : `${schemaUsage.count} schemas`;

    schemaUsageBadge = (
      <HoverCard
        width={240}
        shadow="md"
        withArrow={true}
        position="top"
        openDelay={300}
        closeDelay={100}
        withinPortal={true}
      >
        <HoverCard.Target>
          <div>
            <FormatSchemaCountBadge count={schemaUsage.count} />
          </div>
        </HoverCard.Target>
        <HoverCard.Dropdown>
          <Stack spacing={4}>
            <Text weight={600} size="sm">
              {tooltipHeader}
            </Text>
            {schemaUsage.schemaNames.map((name) => (
              <Text key={name} size="sm" truncate="end">
                {name}
              </Text>
            ))}
          </Stack>
        </HoverCard.Dropdown>
      </HoverCard>
    );
  }

  return (
    <div
      role="presentation"
      className={css(styles.tileContainer)}
      onMouseEnter={() => {
        setHoveredFormatId(formatId);
      }}
      onMouseLeave={() => {
        setHoveredFormatId(null);
      }}
    >
      <button
        className={classNames(css(styles.button), {
          [css(styles.disabledButton)]: !isSelected && disabled,
          [css(styles.selectedButton)]: isSelected,
          [css(styles.archivedButton)]: isArchivedTile,
        })}
        onClick={onClick}
        type="button"
        disabled={disabled}
        role="option"
        aria-selected={isSelected}
        aria-label={`${label} format`}
      >
        <div
          className={classNames(
            css(styles.formatTileGlyphWrapper),
            wrapperClassName,
            getGlyphWrapperClassName(isSelected, disabled, isHovered)
          )}
        >
          {children}
          {schemaUsage != null && <div className={css(styles.schemaCountBadge)}>{schemaUsageBadge}</div>}
        </div>
        <div className={classNames(css(styles.buttonLabel), labelClassName)}>{label}</div>
      </button>
      {overflowMenuOptions != null && overflowMenuOptions.length > 0 && (
        <div className={css(styles.overflowMenuWrapper, isHovered ? styles.overflowMenuVisible : null)}>
          <OverflowMenu
            options={overflowMenuOptions}
            isIconHorizontal={true}
            enableTooltip={false}
            buttonProps={{
              buttonStyle: ButtonStyle.TRANSPARENT,
              'aria-label': `More options for ${label}`,
            }}
            menuProps={{
              width: Menu.WIDTHS.MEDIUM,
              className: css(styles.overflowMenuDropdown),
            }}
          />
        </div>
      )}
    </div>
  );
}

export default function AntibodyFormatPicker({
  antibodyTileProps: antibodyProps,
  disabled,
  selectedFormat,
  setSelectedFormat,
  enableCreateNew = true,
  onFormatCreated,
  onCreationPaneOpenChange,
  analyticsMetadata,
  showAuditLogButton = false,
  onArchiveChange,
  enableFormatManagement = false,
  formatSchemaUsageMap,
}: AntibodyFormatPickerProps): JSX.Element {
  assert(
    analyticsMetadata != null || !enableCreateNew,
    'analyticsMetadata must be provided when creation is enabled'
  );

  const creationPermissionQueryResult = useBenchlingQuery<
    AntibodyFormatCreationPermissionQuery,
    AntibodyFormatCreationPermissionQueryVariables
  >(ANTIBODY_FORMAT_CREATION_PERMISSION_QUERY);

  const [query, setQuery] = useState<string>('');
  const [hoveredFormatId, setHoveredFormatId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [archiveFilter, setArchiveFilter] = useState<ArchiveFilter>('all');
  const [editingFormat, setEditingFormat] = useState<AntibodyFormatGraphQL | null>(null);

  const canManageFormats =
    creationPermissionQueryResult.type === QueryResultType.SUCCESS &&
    creationPermissionQueryResult.data.currentTenant.permissions.canCreateAntibodyFormats;
  const showArchiveFilter = showAuditLogButton;

  const filteredProps = antibodyProps.filter((props) => {
    const matchesSearch = props.format.name.toLowerCase().includes(query.toLowerCase());
    if (!matchesSearch) {
      return false;
    }
    // The schema settings page always shows archive filtering to viewers.
    // Other surfaces continue receiving the full list from their parent.
    if (!showArchiveFilter) {
      return true;
    }
    if (archiveFilter === 'archived') {
      return props.format.archiveData.archived;
    }
    return !props.format.archiveData.archived;
  });

  const handleArchive = (formatId: string, formatName: string): void => {
    ArchiveModal.open({
      items: [{id: formatId, name: formatName}],
      onArchive: async (itemIds: Array<string>, purpose: string) => {
        try {
          await ArchiveDAO.archiveItems(itemIds as Array<CoreObjectId>, purpose);
          ArchiveModal.close();
          notifications.success('Format archived successfully.');
          await onArchiveChange?.();
        } catch (e) {
          notifications.error('Failed to archive format.');
          throw e;
        }
      },
    });
  };

  const handleUnarchive = async (formatId: string): Promise<void> => {
    try {
      await ArchiveDAO.unarchiveItems([formatId as CoreObjectId]);
      notifications.success('Format unarchived successfully.');
      await onArchiveChange?.();
    } catch (e) {
      notifications.error('Failed to unarchive format.');
      throw e;
    }
  };

  function getOverflowMenuOptions(
    format: AntibodyTileProps['format']
  ): Array<OverflowMenuOption> | undefined {
    if (!enableFormatManagement || !canManageFormats || format.builtIn) {
      return undefined;
    }
    if (format.archiveData.archived) {
      return [
        {
          icon: 'unarchive',
          contents: <span>Unarchive format</span>,
          onSelect: () => {
            handleUnarchive(format.id).catch(() => {});
          },
        },
      ];
    }
    return [
      {
        icon: 'edit',
        contents: <span>Edit display name</span>,
        onSelect: () => {
          setEditingFormat(format);
          onCreationPaneOpenChange(true);
        },
      },
      {
        icon: 'archive',
        iconClassName: css(styles.archiveIcon),
        contents: <span className={css(styles.archiveOptionText)}>Archive format</span>,
        onSelect: () => {
          handleArchive(format.id, format.name);
        },
      },
    ];
  }

  if (showCreateModal) {
    assert(analyticsMetadata != null, "analyticsMetadata must be provided to show 'create new' pane");
    return (
      <AntibodyFormatCreationPane
        onRequestClose={() => {
          setShowCreateModal(false);
          onCreationPaneOpenChange(false);
        }}
        onCreate={async (newFormatId) => {
          setShowCreateModal(false);
          onCreationPaneOpenChange(false);
          await onFormatCreated?.(newFormatId);
        }}
        analyticsMetadata={analyticsMetadata}
      />
    );
  }

  if (editingFormat != null) {
    assert(analyticsMetadata != null, 'analyticsMetadata must be provided to show edit pane');
    return (
      <AntibodyFormatCreationPane
        onRequestClose={() => {
          setEditingFormat(null);
          onCreationPaneOpenChange(false);
        }}
        existingFormat={editingFormat}
        onDisplayNameUpdated={async () => {
          await onArchiveChange?.();
        }}
        analyticsMetadata={analyticsMetadata}
      />
    );
  }

  // Exclude built-in formats — they are not stored in the database and have no audit records,
  // which would error when querying the audit log API.
  const auditableFormatIds = showAuditLogButton
    ? antibodyProps
        .filter((props) => !props.format.id.startsWith(BUILTIN_ANTIBODY_FORMAT_ID_PREFIX))
        .map((props) => props.format.id)
    : [];

  return (
    <div {...getTestProps({component: 'AntibodyFormatPicker', element: 'root'})}>
      <Flex mb="sm" align="center" gap="xs" wrap="nowrap">
        <Box style={{flex: '1 1 auto', maxWidth: '50%', minWidth: 0}}>
          <PlainSearchInput
            value={query}
            placeholder="Search by format name"
            onChange={(e: InputChangeEvent) => {
              setQuery(e.target.value);
            }}
            onEscape={() => {
              setQuery('');
            }}
            clearType={ClearType.X}
            borderRadius={BorderRadius.LARGE}
          />
        </Box>
        {showArchiveFilter && (
          <Box style={{flexShrink: 0, minWidth: '180px'}}>
            <SelectDropdown
              displayName={
                ARCHIVE_FILTER_OPTIONS.find((opt) => opt.value === archiveFilter)?.name ?? 'All formats'
              }
              buttonClassName={css(styles.archiveFilterButton)}
              menu={
                <Menu width={Menu.WIDTHS.FULL}>
                  <Section>
                    {ARCHIVE_FILTER_OPTIONS.map((opt) => (
                      <Option
                        key={opt.value}
                        value={opt.value}
                        isSelected={opt.value === archiveFilter}
                        onSelect={(value: string) => {
                          setArchiveFilter(value as ArchiveFilter);
                        }}
                      >
                        {opt.name}
                      </Option>
                    ))}
                  </Section>
                </Menu>
              }
              dataTestAttrs={{
                component: 'AntibodyFormatPicker',
                element: 'archiveFilter',
              }}
            />
          </Box>
        )}
        {showAuditLogButton && (
          <Box style={{flexShrink: 0, marginLeft: 'auto'}}>
            <ExportAuditLogButton
              ids={auditableFormatIds}
              buttonSize={ButtonSize.SMALL}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              additionalOptions={{modalTitleOverride: PROTEIN_FORMAT_AUDIT_LOG_MODAL_TITLE}}
            />
          </Box>
        )}
      </Flex>
      <div className={css(styles.picker)} role="listbox" aria-label="Protein format list">
        {enableCreateNew && archiveFilter !== 'archived' && canManageFormats && (
          <FormatTile
            formatId="create-new-format"
            label="Create new"
            onClick={() => {
              analytics.trackEvent(ANTIBODIES_ANALYTICS_ACTIONS.CLICKED_CREATE_NEW_FORMAT, {
                ...analyticsMetadata,
                veritas_format_preview_enabled: isBiologicsEnabled(),
              });
              if (isBiologicsEnabled()) {
                setShowCreateModal(true);
                onCreationPaneOpenChange(true);
              }
            }}
            isSelected={false}
            isHovered={hoveredFormatId === 'create-new-format'}
            setHoveredFormatId={setHoveredFormatId}
            wrapperClassName={css(styles.createNewGlyphWrapper)}
            labelClassName={css(styles.createNewButtonLabel)}
            disabled={!canUseBiologicsFeature(BiologicsFeature.BIOLOGICS_CREATE)}
          >
            <BenchlingIcon icon="plus" />
          </FormatTile>
        )}
        {!filteredProps.length && (
          <div className={css(styles.noResultsMessageContainer)}>No formats found</div>
        )}
        {filteredProps.map((props) => (
          <FormatTile
            key={props.format.id}
            formatId={props.format.id}
            label={props.format.name}
            onClick={() => {
              if (analyticsMetadata != null) {
                analytics.trackEvent(ANTIBODIES_ANALYTICS_ACTIONS.SELECTED_ANTIBODY_FORMAT_ON_SCHEMA, {
                  ...analyticsMetadata,
                  antibody_format_id: props.format.id,
                });
              }
              setSelectedFormat(props.format.id);
            }}
            isSelected={props.format.id === selectedFormat}
            isHovered={hoveredFormatId === props.format.id}
            disabled={disabled || props.disabled}
            setHoveredFormatId={setHoveredFormatId}
            wrapperClassName={css(styles.formatGlyphWrapper)}
            isArchived={props.format.archiveData.archived}
            overflowMenuOptions={getOverflowMenuOptions(props.format)}
            schemaUsage={
              formatSchemaUsageMap?.get(props.format.id) ??
              (formatSchemaUsageMap != null ? {count: 0, schemaNames: []} : undefined)
            }
          >
            <Antibody {...props} />
          </FormatTile>
        ))}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  picker: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    padding: '16px',
    border: `1px solid ${Color.GRAY_200}`,
    borderRadius: '4px',
    minHeight: '160px',
    maxHeight: '300px',
    overflowY: 'auto',
    position: 'relative',
  },
  tileContainer: {
    position: 'relative',
  },
  button: {
    backgroundColor: Color.WHITE,
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '4px',
    borderRadius: '4px',
    ':hover': {
      backgroundColor: Color.GRAY_100,
    },
    alignItems: 'center',
    maxWidth: '98px', // 90px glyph + 8px padding
  },
  selectedButton: {
    ':disabled': {
      color: Color.BLACK,
    },
  },
  disabledButton: {
    cursor: 'not-allowed',
  },
  archivedButton: {
    opacity: 0.5,
  },
  buttonLabel: {
    display: 'flex',
    justifyContent: 'center',
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  createNewButtonLabel: {
    color: Color.BLUE_450,
  },
  formatTileGlyphWrapper: {
    border: `1px solid ${Color.GRAY_100}`,
    borderRadius: '4px',
    width: '90px',
    height: '90px',
    flex: '0 0 90px',
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    padding: '11px',
    position: 'relative',
  },
  selectedTileGlyphWrapper: {
    borderColor: Color.BLUE_500,
  },
  hoveredTileGlyphWrapper: {
    borderColor: Color.GRAY_200,
  },
  disabledTileGlyphWrapper: {
    opacity: 0.75,
    backgroundColor: Color.GRAY_100,
    borderColor: Color.GRAY_100,
  },
  disabledHoveredTileGlyphWrapper: {
    opacity: 0.75,
    backgroundColor: Color.GRAY_50,
    borderColor: Color.GRAY_200,
  },
  formatGlyphWrapper: {
    backgroundColor: Color.GRAY_50,
  },
  createNewGlyphWrapper: {
    backgroundColor: Color.WHITE,
    color: Color.BLUE_500,
    fontSize: '32px',
  },
  noResultsMessageContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: Color.GRAY_400,
    pointerEvents: 'none',
  },
  schemaCountBadge: {
    position: 'absolute',
    top: '0px',
    left: '3px',
    zIndex: 1,
  },
  overflowMenuWrapper: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    opacity: 0,
    transition: 'opacity 0.1s ease-in-out',
  },
  overflowMenuVisible: {
    opacity: 1,
  },
  overflowMenuDropdown: {
    // Prevent menu options from appearing pre-selected when the menu opens.
    // The shared Menu component applies a :focus background on .is-uncontrolled-hover options,
    // which fires immediately when the portal renders and the <a> element receives focus.
    ':focus-within a:focus': {
      background: 'inherit',
    },
  },
  archiveOptionText: {
    color: Color.RED_500,
  },
  archiveIcon: {
    color: Color.RED_500,
  },
  archiveFilterButton: {
    backgroundColor: Color.GRAY_100,
    borderColor: Color.GRAY_100,
    width: '100%',
    ':hover': {
      backgroundColor: Color.GRAY_200,
      borderColor: Color.GRAY_200,
    },
  },
});
